import {Helmet} from '@dr.pogodin/react-helmet';
import {openDeleteDialog} from 'platform/components';
import {match} from 'ts-pattern';

import {always} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {
  useDeleteServiceCaseTypeMutation,
  usePatchServiceCaseTypeSetAsActiveMutation,
  usePatchServiceCaseTypeSetAsInactiveMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {DmsDataGrid, handleApiError} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

export function ServiceCaseType() {
  const navigate = useNavigate();

  const [patchServiceCaseTypeSetAsActive] = usePatchServiceCaseTypeSetAsActiveMutation();
  const [patchServiceCaseTypeSetAsInactive] = usePatchServiceCaseTypeSetAsInactiveMutation();
  const [deleteServiceCaseType] = useDeleteServiceCaseTypeMutation();

  const actionCallback: ActionCallback = ({actionKey, rowId, refreshData}) => {
    const id = isArray(rowId) ? rowId[0] : rowId;

    match(actionKey)
      .with('redirectDetail', () =>
        navigate(composePath(settingsRoutes.serviceCaseTypeDetail, {params: {id}}))
      )
      .with('setAsActive', () =>
        patchServiceCaseTypeSetAsActive({serviceCaseTypeId: id})
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError)
      )
      .with('setAsInactive', () =>
        patchServiceCaseTypeSetAsInactive({serviceCaseTypeId: id})
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError)
      )
      .with('delete', () =>
        openDeleteDialog({
          onConfirm: () =>
            deleteServiceCaseType({serviceCaseTypeId: id})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
        })
      )
      .otherwise(always(undefined));
  };

  return (
    <>
      <Helmet title={i18n.t('entity.workshopPresetsCategory.labels.serviceCaseType')} />
      <SettingsTemplate
        header={{
          title: i18n.t('entity.workshopPresetsCategory.labels.serviceCaseType'),
          actions: [
            {
              title: i18n.t('entity.serviceCase.actions.newServiceCaseType'),
              onClick: () => navigate(settingsRoutes.serviceCaseTypeCreate),
              type: 'button',
            },
          ],
        }}
        data-testid={testIds.settings.serviceCaseType('template')}
      >
        <DmsDataGrid
          gridCode="service-case-type"
          actionCallback={actionCallback}
          data-testid={testIds.settings.serviceCaseType('datagrid')}
        />
      </SettingsTemplate>
    </>
  );
}
